import React from "react";
import { Reveal } from "@/components/util/Reveal";

const fmt = (n, cur) => (n == null ? "" : `${cur === "USD" ? "US$ " : "AWG "}${Number(n).toLocaleString("en-US")}`);
const PAD = "px-6 sm:px-10 lg:px-16 max-w-[1600px] mx-auto";

const SectionHead = ({ n, heading, body }) => (
  <Reveal className="max-w-4xl">
    <div className="font-display font-black text-[#FF6600] text-sm tracking-[0.2em]">{n}</div>
    <h2 className="mt-4 font-display font-black tracking-tight leading-[0.98] text-4xl sm:text-6xl lg:text-7xl">{heading}</h2>
    {body && <p className="mt-6 text-base sm:text-xl text-[#A3A3A3] leading-relaxed max-w-2xl">{body}</p>}
  </Reveal>
);

const Hero = ({ cfg, hero }) => (
  <section data-testid="pres-s1" className="relative min-h-[100svh] flex items-end overflow-hidden">
    {hero?.image && <img src={hero.image} alt="" className="ur-pres-kenburns absolute inset-0 w-full h-full object-cover opacity-55" />}
    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/75 to-[#0A0A0A]/30" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/80 via-[#0A0A0A]/30 to-transparent" />
    <div className={`relative ${PAD} w-full pb-16 sm:pb-24 pt-40`}>
      <Reveal variant="dramatic">
        <div className="sm:hidden mb-4 text-[11px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3]">{cfg.prepared_for}</div>
        <div className="font-display font-black text-[#FF6600] text-sm tracking-[0.2em]">01</div>
        <h1 data-testid="pres-s1-heading" className="mt-4 font-display font-black tracking-tight leading-[0.92] text-5xl sm:text-7xl lg:text-[112px] max-w-6xl">
          {cfg.s1_heading}
        </h1>
        <p className="mt-8 text-lg sm:text-2xl text-[#D4D4D4] leading-relaxed max-w-3xl">{cfg.s1_body}</p>
      </Reveal>
    </div>
  </section>
);

const Proof = ({ cfg, proof }) => (
  <section data-testid="pres-s2" className={`${PAD} py-24 sm:py-32`}>
    <SectionHead n="02" heading={cfg.s2_heading} body={cfg.s2_body} />
    <div className="mt-14 grid gap-6 md:grid-cols-3">
      {proof.map((p, i) => (
        <Reveal key={p.slug} delay={i * 120} data-testid={`pres-proof-${p.slug}`} className="group">
          <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-[#141414]">
            <img src={p.image} alt={p.client || p.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
          </div>
          <div className="mt-5 font-display font-black text-2xl tracking-tight">{p.client || p.title}</div>
          <div className="mt-1 text-sm text-[#A3A3A3]">{p.category}</div>
          {p.description && <p className="mt-3 text-sm text-[#A3A3A3] leading-relaxed">{p.description}</p>}
        </Reveal>
      ))}
    </div>
    {cfg.established_clients?.length > 0 && (
      <Reveal className="mt-14 pt-8 border-t border-[#1F1F1F] text-sm text-[#7A7A7A]">
        <span className="uppercase tracking-[0.18em] text-xs font-bold text-[#A3A3A3]">Also trusted by</span>
        <span data-testid="pres-established" className="block sm:inline sm:ml-4 mt-2 sm:mt-0">{cfg.established_clients.join(" · ")}</span>
      </Reveal>
    )}
  </section>
);

// Asymmetric editorial sequence: one wide frame, then pairs.
const Sequence = ({ images, label }) => (
  <div className="mt-16 grid gap-5 sm:gap-6 md:grid-cols-12">
    {images.map((src, i) => {
      const span = i % 3 === 0 ? "md:col-span-12 aspect-[16/9]" : `md:col-span-6 aspect-[4/5] ${i % 3 === 2 ? "md:mt-16" : ""}`;
      return (
        <Reveal key={src} delay={(i % 3) * 100} className={`${span} rounded-2xl overflow-hidden bg-[#141414]`}>
          <img src={src} alt={`${label} application ${i + 1}`} loading="lazy" className="w-full h-full object-cover" />
        </Reveal>
      );
    })}
  </div>
);

const Centerpiece = ({ cfg, cp, manual }) => (
  <section data-testid="pres-s3" className="bg-[#0F0F0F] py-24 sm:py-32">
    <div className={PAD}>
      <SectionHead n="03" heading={cfg.s3_heading} body={cfg.s3_body} />
      {cp && (
        <>
          <Reveal className="mt-10 text-sm uppercase tracking-[0.18em] font-bold text-[#FF6600]" data-testid="pres-centerpiece-name">
            {cp.client || cp.title}
          </Reveal>
          <Sequence images={[cp.image, ...cp.images].filter(Boolean)} label={cp.client || cp.title} />
        </>
      )}
      {manual && manual.images.length > 0 && (
        <div data-testid="pres-manual" className="mt-24">
          <Reveal className="font-display font-black text-3xl sm:text-4xl tracking-tight">{cfg.manual_heading} — {manual.client || manual.title}</Reveal>
          <div className="mt-8 grid gap-5 sm:grid-cols-2">
            {manual.images.map((src) => (
              <Reveal key={src} className="rounded-xl overflow-hidden bg-[#141414]">
                <img src={src} alt="" loading="lazy" className="w-full h-auto" />
              </Reveal>
            ))}
          </div>
        </div>
      )}
    </div>
  </section>
);

const Process = ({ cfg, packages }) => (
  <section data-testid="pres-s4" className={`${PAD} py-24 sm:py-32`}>
    <SectionHead n="04" heading={cfg.s4_heading} />
    <ol className="mt-14 grid gap-px bg-[#1F1F1F] rounded-2xl overflow-hidden sm:grid-cols-2 lg:grid-cols-4">
      {cfg.s4_steps.map((s, i) => (
        <Reveal as="li" key={s} delay={i * 100} className="bg-[#0A0A0A] p-8">
          <div className="font-display font-black text-5xl text-[#FF6600]">{i + 1}</div>
          <p className="mt-6 text-lg font-semibold leading-snug">{s}</p>
        </Reveal>
      ))}
    </ol>
    <Reveal className="mt-8 text-sm sm:text-base text-[#A3A3A3] max-w-3xl leading-relaxed">{cfg.s4_note}</Reveal>
    <div className="mt-16 grid gap-6 lg:grid-cols-3">
      {packages.map((p, i) => (
        <Reveal key={p.name} delay={i * 100} data-testid={`pres-package-${i}`} className={`rounded-2xl border p-8 ${i === 1 ? "border-[#FF6600]/60" : "border-[#222]"}`}>
          <div className="font-display font-black text-2xl">{p.name}</div>
          <div className="mt-4 font-display font-black text-4xl">{fmt(p.price_usd, "USD")}</div>
          <div className="mt-1 text-sm text-[#7A7A7A]">{fmt(p.price, "AWG")}</div>
          {p.description && <p className="mt-5 text-sm text-[#A3A3A3] leading-relaxed">{p.description}</p>}
          <ul className="mt-6 space-y-2 text-sm text-[#D4D4D4]">
            {p.features.map((f) => <li key={f} className="flex gap-3"><span className="text-[#FF6600]">—</span>{f}</li>)}
          </ul>
        </Reveal>
      ))}
    </div>
  </section>
);

const Collaborate = ({ cfg, contact }) => (
  <section data-testid="pres-s5" className="bg-[#0F0F0F] py-24 sm:py-32">
    <div className={PAD}>
      <SectionHead n="05" heading={cfg.s5_heading} body={cfg.s5_intro} />
      <ul className="mt-12 grid gap-x-12 gap-y-6 md:grid-cols-2 max-w-5xl">
        {cfg.s5_points.map((pt, i) => (
          <Reveal as="li" key={pt} delay={(i % 2) * 100} className="flex gap-4 text-lg leading-snug border-t border-[#1F1F1F] pt-6">
            <span className="font-display font-black text-[#FF6600]">{String(i + 1).padStart(2, "0")}</span>{pt}
          </Reveal>
        ))}
      </ul>
      <Reveal variant="dramatic" className="mt-28 sm:mt-36" data-testid="pres-closing">
        <h2 className="font-display font-black tracking-tight leading-[0.92] text-6xl sm:text-8xl lg:text-[140px]">
          {cfg.closing_heading.replace(/\.$/, "")}<span className="text-[#FF6600]">.</span>
        </h2>
        <p className="mt-6 text-lg sm:text-2xl text-[#A3A3A3] max-w-2xl">{cfg.closing_body}</p>
        <div className="mt-8 flex flex-wrap gap-x-10 gap-y-2 text-base sm:text-lg">
          {contact.email && <a data-testid="pres-contact-email" href={`mailto:${contact.email}`} className="hover:text-[#FF6600] transition-colors">{contact.email}</a>}
          {contact.phone && <a data-testid="pres-contact-phone" href={`tel:${contact.phone.replace(/[^+\d]/g, "")}`} className="hover:text-[#FF6600] transition-colors">{contact.phone}</a>}
        </div>
      </Reveal>
    </div>
  </section>
);

export const PresentationSections = ({ data }) => (
  <main>
    <Hero cfg={data.config} hero={data.hero} />
    <Proof cfg={data.config} proof={data.proof} />
    <Centerpiece cfg={data.config} cp={data.centerpiece} manual={data.manual} />
    <Process cfg={data.config} packages={data.packages} />
    <Collaborate cfg={data.config} contact={data.contact} />
  </main>
);
